import { motion } from "framer-motion";
import { Clock, Facebook, MapPin, MessageCircle } from "lucide-react";
import { FACEBOOK_URL, MESSENGER_URL } from "../site";

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="bg-[#F0F4F8] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-teal-700 sm:text-sm">
            Contact
          </p>
          <h2 className="font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Parlons de vos textiles
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Un canapé à rafraîchir, un matelas à assainir, une moquette à
            détacher ? Le moyen le plus simple : notre page Facebook.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <motion.div
            data-testid="contact-facebook-card"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-3xl bg-[#0B131E] p-10 lg:col-span-7"
          >
            <div
              className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#1877F2]/20 blur-3xl"
              aria-hidden="true"
            />
            <Facebook className="h-12 w-12 text-[#1877F2]" />
            <h3 className="mt-6 font-serif text-2xl font-semibold text-white sm:text-3xl">
              Notre page Facebook, votre ligne directe
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
              Envoyez une photo de votre canapé, matelas ou moquette, indiquez
              les dimensions : nous vous répondons avec un tarif précis et une
              proposition de rendez-vous.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-messenger-link"
                className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#166FE5] hover:shadow-xl"
              >
                <MessageCircle className="h-4 w-4" />
                Ouvrir Messenger
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-400 hover:text-teal-300"
              >
                <Facebook className="h-4 w-4" />
                Voir la page
              </a>
            </div>
          </motion.div>

          <motion.div
            data-testid="contact-info-hours"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="flex flex-col gap-6 lg:col-span-5"
          >
            <div className="flex-1 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <Clock className="h-8 w-8 text-teal-600" />
              <h4 className="mt-4 font-serif text-xl font-semibold text-slate-900">
                Disponibilités
              </h4>
              <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
                <li className="flex justify-between gap-4">
                  <span>Lundi – Vendredi</span>
                  <span className="font-semibold text-slate-900">8h00 – 19h00</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Samedi</span>
                  <span className="font-semibold text-slate-900">9h00 – 17h00</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Dimanche</span>
                  <span className="font-semibold text-slate-900">8h00 – 20h00 · sur RDV</span>
                </li>
              </ul>
            </div>
            <div className="flex-1 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <MapPin className="h-8 w-8 text-teal-600" />
              <h4 className="mt-4 font-serif text-xl font-semibold text-slate-900">
                Intervention à domicile
              </h4>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Nous venons chez vous avec tout le matériel professionnel —
                maison, appartement ou parking pour votre véhicule.
              </p>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="contact-zones">
                {["Chapareillan", "Chambéry", "Aix-les-Bains", "Annecy", "Annemasse"].map((commune) => (
                  <span
                    key={commune}
                    className="rounded-full border border-teal-100 bg-teal-50 px-3.5 py-1.5 text-xs font-semibold text-teal-800"
                  >
                    {commune}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Et communes alentour sur demande, via Messenger.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
